import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function UserManagement() {
    const [users, setUsers] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        phone: "",
    });
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const navigate = useNavigate();

    const [showPasswordPopup, setShowPasswordPopup] = useState(false);
    const [newPassword, setNewPassword] = useState("");

    const fetchUsers = async () => {
        try {
            const res = await axios.get("https://api.pdf.tajgateways.com/api-v1/users/getUsers");
            setUsers(res.data.data || []);
        } catch (err) {
            console.error("Error fetching users:", err);
            toast.error("❌ Failed to fetch users");
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEdit) {
                const res = await axios.put(
                    `https://api.pdf.tajgateways.com/api-v1/users/editUser/${selectedUserId}`,
                    formData
                );
                if (res.data.code === 200) toast.success("✅ User updated successfully!");
                else toast.error("❌ Failed to update user!");
            } else {
                const res = await axios.post(
                    "https://api.pdf.tajgateways.com/api-v1/users/addUser",
                    formData
                );
                if (res.data.code === 200) toast.success("✅ User added successfully!");
                else toast.error("❌ Failed to add user!");
            }

            setShowForm(false);
            setFormData({ name: "", email: "", password: "", phone: "" });
            setIsEdit(false);
            setSelectedUserId(null);
            fetchUsers();
        } catch (err) {
            console.error("Error saving user:", err);
            toast.error("❌ Error saving user");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setFormData({
            name: user.name,
            email: user.email,
            password: "",
            phone: user.phone || "",
        });
        setSelectedUserId(user._id);
        setIsEdit(true);
        setShowForm(true);
    };

    const confirmDelete = (user) => {
        setUserToDelete(user);
        setShowDeletePopup(true);
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        try {
            await axios.delete(
                `https://api.pdf.tajgateways.com/api-v1/users/deleteUser/${userToDelete._id}`
            );
            toast.success("✅ User deleted successfully!");
            setShowDeletePopup(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (err) {
            console.error("Error deleting user:", err);
            toast.error("❌ Error deleting user");
        }
    };

    const handleChangePassword = async () => {
        if (!newPassword) {
            toast.error("❌ Please enter new password!");
            return;
        }
        try {
            const res = await axios.put(
                `https://api.pdf.tajgateways.com/api-v1/users/changePassword/${selectedUserId}`,
                { password: newPassword }
            );
            if (res.data.code === 200) {
                toast.success("✅ Password updated successfully!");
                setShowPasswordPopup(false);
                setNewPassword("");
            } else {
                toast.error("❌ Failed to update password!");
            }
        } catch (err) {
            console.error("Error updating password:", err);
            toast.error("❌ Error updating password");
        }
    };

    return (
        <div style={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            <div style={styles.header}>
                <h2 style={styles.title}>👥 User Management</h2>
                <div style={styles.actions}>
                    <button
                        style={styles.addBtn}
                        onClick={() => {
                            setShowForm(true);
                            setIsEdit(false);
                            setFormData({ name: "", email: "", password: "", phone: "" });
                        }}
                    >
                        ➕ Add User
                    </button>
                    <button
                        style={styles.dashboardBtn}
                        onClick={() => navigate("/")}
                    >
                        📊 Dashboard
                    </button>
                </div>
            </div>

            {showForm && (
                <div style={styles.formContainer}>
                    <form style={styles.form} onSubmit={handleSubmit}>
                        <h3 style={styles.formTitle}>
                            {isEdit ? "✏️ Edit User" : "➕ Add New User"}
                        </h3>

                        <input style={styles.input} type="text" name="name" placeholder="Name" value={formData.name} onChange={handleChange} required />
                        <input style={styles.input} type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required disabled={isEdit} />
                        {!isEdit && (
                            <input style={styles.input} type="password" name="password" placeholder="Password" value={formData.password} onChange={handleChange} required />
                        )}
                        <input style={styles.input} type="text" name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} />

                        <button style={styles.button} type="submit" disabled={loading}>
                            {loading ? "⏳ Submitting..." : isEdit ? "Update User" : "Add User"}
                        </button>
                    </form>
                </div>
            )}

            <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Email</th>
                            <th style={styles.th}>Phone</th>
                            <th style={styles.th}>Role</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length > 0 ? (
                            users.map((user) => (
                                <tr key={user._id}>
                                    <td style={styles.td}>{user.name}</td>
                                    <td style={styles.td}>{user.email}</td>
                                    <td style={styles.td}>{user.phone || "-"}</td>
                                    <td style={styles.td}>{user.role}</td>
                                    <td style={styles.td}>
                                        <button style={styles.editBtn} onClick={() => handleEdit(user)}>✏️</button>
                                        <button style={styles.passwordBtn} onClick={() => { setSelectedUserId(user._id); setShowPasswordPopup(true); }}>🔑</button>
                                        <button style={styles.deleteBtn} onClick={() => confirmDelete(user)}>🗑️</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" style={styles.noData}>No users found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showDeletePopup && (
                <div style={styles.popupOverlay}>
                    <div style={styles.popup}>
                        <h3>⚠️ Confirm Delete</h3>
                        <p>Are you sure you want to delete <strong>{userToDelete?.name}</strong>?</p>
                        <div style={styles.popupActions}>
                            <button style={styles.cancelBtn} onClick={() => setShowDeletePopup(false)}>❌ Cancel</button>
                            <button style={styles.confirmBtn} onClick={handleDelete}>✅ Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {showPasswordPopup && (
                <div style={styles.popupOverlay}>
                    <div style={styles.popup}>
                        <h3>🔑 Change Password</h3>
                        <input style={styles.input} type="password" placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                        <div style={styles.popupActions}>
                            <button style={styles.cancelBtn} onClick={() => setShowPasswordPopup(false)}>❌ Cancel</button>
                            <button style={styles.confirmBtn} onClick={handleChangePassword}>✅ Update</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Responsive CSS */}
            <style>
                {`
                    @media (max-width: 768px) {
                        form { width: 90% !important; padding: 15px; }
                        .actions { flex-direction: column; gap: 10px; }
                        table { font-size: 13px; }
                        th, td { padding: 8px; }
                        button { padding: 8px; font-size: 14px; }
                    }
                    @media (max-width: 480px) {
                        form { width: 95% !important; }
                        input { padding: 8px; font-size: 13px; }
                    }
                `}
            </style>
        </div>
    );
}

const styles = {
    container: { padding: "20px" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" },
    title: { fontSize: "24px" },
    actions: { display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" },
    addBtn: { background: "#3498db", color: "#fff", padding: "10px 15px", border: "none", borderRadius: "5px", cursor: "pointer" },
    editBtn: { background: "#f39c12", color: "#fff", padding: "5px 10px", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "5px" },
    passwordBtn: { background: "#9b59b6", color: "#fff", padding: "5px 10px", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "5px" },
    deleteBtn: { background: "#e74c3c", color: "#fff", padding: "5px 10px", border: "none", borderRadius: "4px", cursor: "pointer" },
    formContainer: { margin: "20px 0", display: "flex", justifyContent: "center" },
    form: { background: "#fff", padding: "20px", borderRadius: "10px", boxShadow: "0 6px 20px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", gap: "15px", width: "400px" },
    formTitle: { textAlign: "center", color: "#2c3e50" },
    input: { padding: "10px", borderRadius: "5px", border: "1px solid #ccc", fontSize: "14px" },
    button: { background: "#3498db", color: "#fff", padding: "10px", border: "none", borderRadius: "5px", cursor: "pointer" },
    table: { width: "100%", borderCollapse: "collapse", marginTop: "20px" },
    th: { padding: "12px", background: "#3498db", color: "#fff", textAlign: "left" },
    td: { padding: "12px", borderBottom: "1px solid #ddd" },
    noData: { textAlign: "center", padding: "20px" },
    popupOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center" },
    popup: { background: "#fff", padding: "20px", borderRadius: "10px", textAlign: "center", boxShadow: "0 6px 20px rgba(0,0,0,0.3)", width: "300px" },
    popupActions: { marginTop: "20px", display: "flex", justifyContent: "space-around" },
    cancelBtn: { background: "#7f8c8d", color: "#fff", padding: "8px 12px", border: "none", borderRadius: "5px", cursor: "pointer" },
    confirmBtn: { background: "#2ecc71", color: "#fff", padding: "8px 12px", border: "none", borderRadius: "5px", cursor: "pointer" },
    dashboardBtn: { background: "#3498db", color: "#fff", padding: "8px 14px", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "500" },
};
